/**
 * Page-context reporter core (framework-free, unit-tested):
 * debounce -> capture screen -> dedupe identical snapshots -> seq++ -> ring buffer of
 * the last 3 pages -> fit to 31 KB -> send. Sending never throws (send must swallow errors).
 *
 * usePageContextReporter feeds it snapshots; `repost()` re-sends the current page
 * (AI panel opened, heartbeat, tab visible again) even if nothing changed, so the
 * server copy (TTL 15 min) stays fresh.
 *
 * `getScreen` (plan 1968 F13) is called at SEND time, so the screen snapshot is as
 * fresh as the post. Dedupe ignores its capture time: an unchanged screen is not re-sent.
 */

import { fitPageContext } from "./fitPageContext";
import {
  PAGE_CONTEXT_LIMITS,
  PAGE_CONTEXT_VERSION,
  type PageContext,
  type PageRef,
  type PageSnapshot,
  type ScreenContext,
} from "./types";

export const PAGE_CONTEXT_DEBOUNCE_MS = 400;
export const PAGE_CONTEXT_HEARTBEAT_MS = 4 * 60 * 1000;

export interface PageContextReporterOptions {
  send: (context: PageContext) => Promise<unknown>;
  debounceMs?: number;
  now?: () => Date;
  tabId?: string;
  /** What the page shows right now (undefined = omit `screen`, e.g. AI panel closed) */
  getScreen?: (path: string, at: string) => ScreenContext | undefined;
}

export interface PageContextReporter {
  /** Queue the latest snapshot (debounced; identical snapshots are not re-sent). */
  update: (snapshot: PageSnapshot) => void;
  /** Send now: the pending snapshot, else the last one again (new seq/at). */
  repost: () => void;
  /** Forget history (tenant/user changed); cancels anything pending. */
  reset: () => void;
  /** Stop timers (unmount). */
  dispose: () => void;
  /** For tests/diagnostics. */
  readonly lastSent: PageContext | null;
}

export function createTabId(): string {
  try {
    return crypto.randomUUID().slice(0, 12);
  } catch {
    return Math.random().toString(36).slice(2, 14);
  }
}

function toRef(snapshot: PageSnapshot, at: string): PageRef {
  return {
    path: snapshot.path,
    pageType: snapshot.pageType,
    ...(snapshot.doctype ? { doctype: snapshot.doctype } : {}),
    ...(snapshot.record?.name ? { name: snapshot.record.name } : {}),
    ...(snapshot.title ? { title: snapshot.title } : {}),
    at,
  };
}

export function createPageContextReporter(options: PageContextReporterOptions): PageContextReporter {
  const debounceMs = options.debounceMs ?? PAGE_CONTEXT_DEBOUNCE_MS;
  const now = options.now ?? (() => new Date());
  const tabId = options.tabId ?? createTabId();

  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: PageSnapshot | null = null;
  let lastSnapshot: PageSnapshot | null = null;
  let lastKey: string | null = null;
  let lastSent: PageContext | null = null;
  let seq = 0;
  let recent: PageRef[] = [];
  let current: PageRef | null = null;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const captureScreen = (path: string, at: string): ScreenContext | undefined => {
    try {
      return options.getScreen?.(path, at);
    } catch {
      return undefined;
    }
  };

  const send = (snapshot: PageSnapshot, force: boolean) => {
    const at = now().toISOString();
    const screen = captureScreen(snapshot.path, at);
    const key = JSON.stringify(snapshot) + (screen ? JSON.stringify({ ...screen, at: undefined }) : "");
    if (!force && key === lastKey) return;

    // Ring buffer: when the page changes, the page we were on becomes "recent"
    if (current && current.path !== snapshot.path) {
      recent = [current, ...recent.filter((r) => r.path !== current!.path && r.path !== snapshot.path)].slice(
        0,
        PAGE_CONTEXT_LIMITS.maxRecent,
      );
    }
    current = toRef(snapshot, current && current.path === snapshot.path ? current.at : at);

    seq += 1;
    const context = fitPageContext({
      ...snapshot,
      ...(screen ? { screen } : {}),
      v: PAGE_CONTEXT_VERSION,
      seq,
      at,
      tabId,
      recent: [...recent],
    });
    lastKey = key;
    lastSnapshot = snapshot;
    lastSent = context;
    void Promise.resolve()
      .then(() => options.send(context))
      .catch(() => undefined);
  };

  const flush = () => {
    clearTimer();
    if (!pending) return;
    const snapshot = pending;
    pending = null;
    send(snapshot, false);
  };

  return {
    update(snapshot) {
      pending = snapshot;
      clearTimer();
      timer = setTimeout(flush, debounceMs);
    },
    repost() {
      if (pending) {
        const snapshot = pending;
        pending = null;
        clearTimer();
        send(snapshot, true);
        return;
      }
      if (lastSnapshot) send(lastSnapshot, true);
    },
    reset() {
      clearTimer();
      pending = null;
      lastSnapshot = null;
      lastKey = null;
      lastSent = null;
      recent = [];
      current = null;
    },
    dispose() {
      clearTimer();
      pending = null;
    },
    get lastSent() {
      return lastSent;
    },
  };
}
